// src/pages/UnsoldTicketAppraisal/modals/RecallAppraisalDetailModal.tsx
// Mục 22, REQUIRE_FIXBUGS.md — modal chi tiết phiếu thu hồi vé ế (TicketRecall) trong màn thẩm
// định: thông tin phiếu + dải số + (nếu đã có) kết quả thẩm định. Thay thế
// ReconciliationDetailModal.tsx của luồng cũ (TicketReturnReconciliation, không còn dữ liệu).

import React, { useEffect, useState } from 'react';
import { Button, Collapse, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import moment from 'moment';
import { AgentMap, getAgentCode, getAgentName } from 'helpers/agentLookup';
import {
  TICKET_RECALL_STATUS,
  TICKET_RECALL_STATUS_COLORS,
  TICKET_RECALL_STATUS_LABELS,
  TicketRecall,
} from 'pages/TicketRecall/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('unsold_ticket_appraisal');

interface RecallAppraisalDetailModalProps {
  isOpen: boolean;
  item: TicketRecall | null;
  agentMap: AgentMap;
  loading?: boolean;
  onClose: () => void;
}

const formatNumber = (value?: number | null) =>
  typeof value === 'number' ? value.toLocaleString('vi-VN') : '0';

const formatCurrency = (value?: number | null) =>
  typeof value === 'number' ? st('value_d_text', { value: value.toLocaleString('vi-VN') }) : '-';

const COLLAPSE_THRESHOLD = 10;

const RecallAppraisalDetailModal: React.FC<RecallAppraisalDetailModalProps> = ({
  isOpen,
  item,
  agentMap,
  loading = false,
  onClose,
}) => {
  const lineCount = item?.lines?.length || 0;
  const [linesOpen, setLinesOpen] = useState(true);

  useEffect(() => {
    if (isOpen) setLinesOpen(lineCount <= COLLAPSE_THRESHOLD);
  }, [isOpen, lineCount]);

  if (!item) return null;

  const statusColor = item.statusBadgeColor || TICKET_RECALL_STATUS_COLORS[String(item.status)] || 'secondary';
  const statusLabel = item.statusName || TICKET_RECALL_STATUS_LABELS[String(item.status)] || st('khong_ro_text');
  const isAppraised =
    Number(item.status) === TICKET_RECALL_STATUS.Approved ||
    Number(item.status) === TICKET_RECALL_STATUS.Rejected;

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>{st('chi_tiet_phieu_thu_hoi_title')} {item.recallCode}</ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-bordered table-sm mb-0">
                <tbody>
                  <tr>
                    <td width="30%" className="fw-semibold bg-light">
                      {st('ma_phieu_text')}
                    </td>
                    <td>{item.recallCode}</td>
                  </tr>
                  <tr>
                    <td className="fw-semibold bg-light">{st('trang_thai_column')}</td>
                    <td>
                      <span className={`badge bg-${statusColor}-subtle text-${statusColor} text-uppercase`}>
                        {statusLabel}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-semibold bg-light">{st('ke_hoach_phat_hanh_column')}</td>
                    <td>{item.planCode || item.issuancePlanId}</td>
                  </tr>
                  <tr>
                    <td className="fw-semibold bg-light">{st('nguon_thu_hoi_column')}</td>
                    <td>
                      {item.agentId ? (
                        <>
                          {getAgentName(agentMap, item.agentId)}{' '}
                          <small className="text-muted">({getAgentCode(agentMap, item.agentId)})</small>
                        </>
                      ) : (
                        st('kho_tong_text')
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-semibold bg-light">{st('tong_so_ve_text')}</td>
                    <td className="fw-semibold">{formatNumber(item.totalQuantity)}</td>
                  </tr>
                  <tr>
                    <td className="fw-semibold bg-light">{st('nguoi_lap_ngay_lap_text')}</td>
                    <td>
                      {item.createdByName || '-'}
                      {item.createdAt ? ` — ${moment(item.createdAt).format('DD/MM/YYYY HH:mm')}` : ''}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-semibold bg-light">{st('nguoi_xac_nhan_ngay_xac_nhan_text')}</td>
                    <td>
                      {item.confirmedByName || '-'}
                      {item.confirmedAt ? ` — ${moment(item.confirmedAt).format('DD/MM/YYYY HH:mm')}` : ''}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-semibold bg-light">{st('ghi_chu_text')}</td>
                    <td>{item.note || '-'}</td>
                  </tr>
                  {isAppraised && (
                    <tr>
                      <td className="fw-semibold bg-light">{st('nguoi_ngay_duyet_text')}</td>
                      <td>
                        {item.appraisedByName || '-'}
                        {item.appraisedAt
                          ? ` — ${moment(item.appraisedAt).format('DD/MM/YYYY HH:mm')}`
                          : ''}
                      </td>
                    </tr>
                  )}
                  {item.appraisalComment && (
                    <tr>
                      <td className="fw-semibold bg-light">{st('ghi_chu_duyet_ly_do_tu_text')}</td>
                      <td>{item.appraisalComment}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex align-items-center justify-content-between mt-3 mb-2">
              <h6 className="mb-0">{st('chi_tiet_dai_so_title')}{formatNumber(lineCount)} {st('dong_title')}</h6>
              <Button
                color="link"
                size="sm"
                className="p-0 text-decoration-none"
                type="button"
                aria-expanded={linesOpen}
                onClick={() => setLinesOpen((prev) => !prev)}
              >
                {linesOpen ? st('thu_gon_text') : st('mo_rong_text')}
                <i
                  className={`${linesOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} align-bottom ms-1`}
                ></i>
              </Button>
            </div>

            <Collapse isOpen={linesOpen}>
              <div className="table-responsive">
                <table className="table table-sm table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>{st('ky_hieu_column')}</th>
                      <th>{st('tu_so_column')}</th>
                      <th>{st('den_so_column')}</th>
                      <th className="text-end">{st('so_luong_column')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineCount === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-3">
                          {st('khong_co_dong_dai_so_text')}
                        </td>
                      </tr>
                    ) : (
                      (item.lines || []).map((line, index) => (
                        <tr key={line.id ?? index}>
                          <td>{line.symbol}</td>
                          <td>{line.rangeStart}</td>
                          <td>{line.rangeEnd}</td>
                          <td className="text-end">{formatNumber(line.quantity)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Collapse>

            {item.agentId && (
              <>
                {/* Mục 11.b, README_UPDATE.md — chi tiết các phiếu giao gốc mà phiếu thu hồi này
                    "ăn" theo, phục vụ đối chiếu giao → thu hồi ngay trong màn thẩm định. */}
                <h6 className="mt-4 mb-2">
                  {st('phieu_giao_goc_title')}{formatNumber(item.sourceDeliveries?.length || 0)} {st('phieu_title')}
                </h6>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{st('ma_phieu_giao_column')}</th>
                        <th>{st('thoi_gian_giao_column')}</th>
                        <th>{st('ky_hieu_column')}</th>
                        <th>{st('dai_so_column')}</th>
                        <th className="text-end">{st('tong_ve_giao_column')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!item.sourceDeliveries || item.sourceDeliveries.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center text-muted py-3">
                            {st('khong_xac_dinh_duoc_phieu_giao_text')}
                          </td>
                        </tr>
                      ) : (
                        item.sourceDeliveries.map((delivery, index) => (
                          <tr key={`${delivery.deliveryCode}-${index}`}>
                            <td className="fw-medium text-primary">{delivery.deliveryCode}</td>
                            <td>
                              {delivery.deliveredAt
                                ? moment(delivery.deliveredAt).format('DD/MM/YYYY HH:mm')
                                : '-'}
                            </td>
                            <td>{delivery.symbol}</td>
                            <td>
                              {delivery.rangeStart} - {delivery.rangeEnd}
                            </td>
                            <td className="text-end">{formatNumber(delivery.quantity)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Mục 11.b — so sánh Giao/Thu hồi/Bán và công nợ ước tính của CẢ đại lý trong cùng
                    kế hoạch (không chỉ riêng phiếu đang xem). */}
                <h6 className="mt-4 mb-2">{st('doi_chieu_cong_no_dai_ly_title')}</h6>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0">
                    <tbody>
                      <tr>
                        <td className="fw-semibold bg-light" width="40%">{st('tong_ve_da_giao_text')}</td>
                        <td>{formatNumber(item.agentTotalDeliveredQuantity)}</td>
                      </tr>
                      <tr>
                        <td className="fw-semibold bg-light">{st('tong_ve_da_thu_hoi_text')}</td>
                        <td>{formatNumber(item.agentTotalRecalledQuantity)}</td>
                      </tr>
                      <tr>
                        <td className="fw-semibold bg-light">{st('so_ve_da_ban_duoc_text')}</td>
                        <td className="text-success fw-semibold">
                          {formatNumber(item.agentTotalSoldQuantity)}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-semibold bg-light">{st('cong_no_uoc_tinh_text')}</td>
                        <td className="fw-semibold">{formatCurrency(item.agentDebtValue)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>
          {st('dong_button')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default RecallAppraisalDetailModal;
